import { SrCustomerStatus } from '@plato/data/starrocks/types';
import { Highlight } from '@plato/ui/primitives/highlight';
import { Typography } from '@plato/ui/primitives/typography';
import { Slot } from '@radix-ui/react-slot';
import { XIcon } from 'lucide-react';
import type { PropsWithChildren, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { useDebounce } from 'react-use';
import { CustomerIcon } from '../../../../assets/icons/customer-icon.tsx';
import { OrderIcon } from '../../../../assets/icons/order-icon.tsx';
import { ProductIcon } from '../../../../assets/icons/product-icon.tsx';
import { QuoteIcon } from '../../../../assets/icons/quote-icon.tsx';
import { Button } from '../../../../components/ui/button.tsx';
import {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandLoading,
} from '../../../../components/ui/command.tsx';
import { LinkText } from '../../../../components/ui/link-text.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover.tsx';
import { SelectTriggerButton } from '../../../../components/ui/select-trigger-button.tsx';
import Spinner from '../../../../components/ui/spinner.tsx';
import { TabsHeader } from '../../../../components/ui/tabs-header.tsx';
import Text from '../../../../components/ui/text.tsx';
import type { UseControlledStateProps } from '../../../../lib/hooks/use-controlled-state.ts';
import { useControlledState } from '../../../../lib/hooks/use-controlled-state.ts';
import { useLocale } from '../../../../lib/hooks/use-locale.ts';
import { GlobalSearchTab, SearchEntity, SearchEntityTab } from '../../../../server/types/search-types.ts';
import {
    useSearchEntityData,
    type SearchEntityItem,
    type SearchTracking,
} from '../../hooks/use-search-entity.ts';

export type { SearchTracking };

type EntitySearchContentProps = {
    entities: SearchEntity[];
    inputPlaceholder?: string;
    onItemSelected?: (item: SearchEntityItem) => void;
    search?: UseControlledStateProps<string>['state'];
    setSearch?: UseControlledStateProps<string>['setState'];
    initialTab?: GlobalSearchTab;
    tracking: SearchTracking;
};

const ENTITY_ICONS = {
    [SearchEntity.Customer]: CustomerIcon,
    [SearchEntity.SalesOrder]: OrderIcon,
    [SearchEntity.SalesQuote]: QuoteIcon,
    [SearchEntity.Product]: ProductIcon,
} as const;

const NON_ACTIVE_STATUS_COLOR: Record<string, 'text-blue-600' | 'text-orange-600' | 'text-red-600'> = {
    [SrCustomerStatus.NEW]: 'text-blue-600',
    [SrCustomerStatus.AT_RISK]: 'text-orange-600',
    [SrCustomerStatus.INACTIVE]: 'text-red-600',
    [SrCustomerStatus.DORMANT]: 'text-red-600',
};

export const EntitySearchContent = ({
    entities,
    onItemSelected,
    inputPlaceholder,
    initialTab,
    tracking: trackingProp,
    ...props
}: EntitySearchContentProps): ReactNode => {
    const { t } = useLocale();

    const nonActiveStatusLabels: Record<string, string> = {
        [SrCustomerStatus.NEW]: t('status_new', { ns: 'common' }),
        [SrCustomerStatus.AT_RISK]: t('status_at_risk', { ns: 'common' }),
        [SrCustomerStatus.INACTIVE]: t('status_inactive', { ns: 'common' }),
        [SrCustomerStatus.DORMANT]: t('status_dormant', { ns: 'common' }),
    };

    const [search, setSearch] = useControlledState({
        state: props.search,
        setState: props.setSearch,
    });
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const listRef = useRef<HTMLDivElement>(null);

    const [selectedTabKey, setSelectedTabKey] = useState<GlobalSearchTab>(() => {
        if (initialTab) {
            return initialTab;
        }
        return entities.length == 1 && entities[0] ? SearchEntityTab[entities[0]] : GlobalSearchTab.All;
    });

    const tabs =
        entities.length > 1
            ? [GlobalSearchTab.All, ...entities.map((entity) => SearchEntityTab[entity])]
            : [];

    const [, cancel] = useDebounce(
        () => {
            setDebouncedSearch(search);

            requestAnimationFrame(() => {
                listRef.current?.scrollTo({ top: 0 });
            });
        },
        250,
        [search],
    );

    const { data, isLoading, isFetched, trackResultClicked } = useSearchEntityData({
        entities,
        search: debouncedSearch,
        selectedTabKey,
        tracking: trackingProp,
    });

    const handleSearchChange = (value: string): void => {
        setSearch(value);
        cancel();
    };

    const handleItemSelected = (item: SearchEntityItem): void => {
        trackResultClicked(item);
        onItemSelected?.(item);
    };

    return (
        <>
            <CommandInput
                placeholder={inputPlaceholder || t('top_navbar__search-placeholder')}
                value={search}
                onValueChange={handleSearchChange}
                className="border-0 pb-0"
            />
            <TabsHeader<GlobalSearchTab>
                className="px-4"
                selectedTabKey={selectedTabKey}
                onTabChange={function (tabId: GlobalSearchTab): void {
                    setSelectedTabKey(tabId);
                }}
                tabs={tabs}
                getTabLabel={(tabId: string) => {
                    return t(`search__label__${tabId}`, { ns: 'common' });
                }}
            />
            <CommandList ref={listRef} className="w-full">
                {isFetched && <CommandEmpty>{t('nothing_found')}</CommandEmpty>}
                {isLoading && (
                    <CommandLoading>
                        <Spinner size={10} />
                    </CommandLoading>
                )}

                {[...data.entries()]
                    .filter((value) => value[1].length)
                    .map(([entity, items]) => (
                        <CommandGroup
                            key={entity}
                            heading={
                                selectedTabKey === GlobalSearchTab.All
                                    ? t(`search__label__${SearchEntityTab[entity]}`)
                                    : undefined
                            }
                        >
                            {items.map((item) => {
                                const { id, name, subtitle, caption } = item;
                                const subNameText = item.highlight?.subtitle?.text ?? subtitle?.text;
                                const extraText = item.highlight?.caption?.text ?? caption?.text;
                                const Icon = ENTITY_ICONS[entity];
                                const itemStatus = item.status;
                                const showCustomerStatus =
                                    entity === SearchEntity.Customer &&
                                    itemStatus &&
                                    itemStatus !== SrCustomerStatus.ACTIVE;

                                return (
                                    <CommandItem
                                        key={id}
                                        value={id}
                                        onSelect={() => {
                                            handleItemSelected(item);
                                        }}
                                    >
                                        <div className="flex flex-1 gap-2">
                                            <Icon className="flex h-4! w-4! shrink-0" />
                                            <div className="flex flex-col text-left flex-1">
                                                <Highlight
                                                    text={item.highlight?.name?.text ?? name}
                                                    className="leading-tight"
                                                />
                                                {item.highlight?.match && (
                                                    <Typography
                                                        variant="body"
                                                        className="text-content-weaker"
                                                    >
                                                        <Highlight
                                                            text={
                                                                item.highlight.match.prefix
                                                                    ? `${item.highlight.match.prefix}: ${item.highlight.match.text}`
                                                                    : item.highlight.match.text
                                                            }
                                                        />
                                                    </Typography>
                                                )}
                                                {(subNameText || showCustomerStatus) && (
                                                    <Typography variant="caption">
                                                        {subNameText && <Highlight text={subNameText} />}
                                                        {subNameText && showCustomerStatus && ' · '}
                                                        {showCustomerStatus && (
                                                            <span
                                                                className={
                                                                    NON_ACTIVE_STATUS_COLOR[itemStatus]
                                                                }
                                                            >
                                                                {nonActiveStatusLabels[itemStatus]}
                                                            </span>
                                                        )}
                                                    </Typography>
                                                )}
                                            </div>
                                            {extraText && (
                                                <Typography
                                                    variant="body"
                                                    className="text-content-weaker max-w-60 truncate"
                                                >
                                                    <Highlight text={extraText} />
                                                </Typography>
                                            )}
                                        </div>
                                    </CommandItem>
                                );
                            })}

                            {selectedTabKey === GlobalSearchTab.All && items.length >= 6 && (
                                <CommandItem
                                    onSelect={() => {
                                        setSelectedTabKey(SearchEntityTab[entity]);
                                    }}
                                >
                                    <LinkText id="show-more-text" className="flex">
                                        {t('show_more')}
                                    </LinkText>
                                </CommandItem>
                            )}
                        </CommandGroup>
                    ))}
            </CommandList>
        </>
    );
};

export type EntitySearchComboboxProps = {
    entities: SearchEntity[];
    className?: string;
    setOpen?: UseControlledStateProps<boolean>['setState'];
    open?: UseControlledStateProps<boolean>['state'];
    inputPlaceholder?: string;
    onValueChange?: (value: SearchEntityItem[]) => void;
    onClear?: () => void;
    defaultValue?: Omit<SearchEntityItem, 'original'>[];
    multiple?: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    variant?: 'default' | 'inline';
    error?: boolean;
    tracking: SearchTracking;
} & PropsWithChildren;

export const EntitySearchCombobox = ({
    entities,
    className,
    children,
    inputPlaceholder,
    onValueChange,
    onClear,
    defaultValue,
    multiple = false,
    isLoading,
    disabled,
    variant = 'default',
    error = false,
    tracking,
    ...props
}: EntitySearchComboboxProps): ReactNode => {
    const [open, setOpen] = useControlledState({
        state: props.open,
        setState: props.setOpen,
    });
    const [search, setSearch] = useState('');

    const [selectedItems, setSelectedItems] = useState<SearchEntityItem[]>([]);

    const ref = useRef<HTMLDivElement>(null);

    const itemsToRender = (selectedItems.length > 0 ? selectedItems : defaultValue) ?? [];

    const handleClear = onClear
        ? () => {
              setSelectedItems([]);
              setOpen(false);
              onClear();
          }
        : undefined;

    const triggerContent = children ?? (
        <SelectTriggerButton variant={variant} isLoading={isLoading} disabled={disabled} error={error}>
            {itemsToRender.length > 0 ? (
                <div className="flex w-full items-center gap-1">
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                        {itemsToRender.map((item) => (
                            <Text key={item.id} className="text-left whitespace-normal truncate">
                                {[SearchEntity.SalesOrder, SearchEntity.SalesQuote].includes(item.entity)
                                    ? item.name
                                    : `${item.id} ${item.name}`}
                            </Text>
                        ))}
                    </div>
                    {handleClear && !disabled && (
                        <Button
                            variant="ghost"
                            icon={XIcon}
                            className="shrink-0 p-0 h-6 w-6 min-w-6 opacity-50 hover:opacity-100"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                        />
                    )}
                </div>
            ) : (
                <Text color="neutral-600" className="text-left">
                    {inputPlaceholder ?? 'Select an item'}
                </Text>
            )}
        </SelectTriggerButton>
    );

    const handleItemSelected = (item: SearchEntityItem): void => {
        if (!multiple) {
            setOpen(false);
        }

        const _items = ((prevItems) => {
            if (!multiple) {
                // If not multiple, clear previous items and add the new one
                return [item];
            }

            const existingItemIndex = prevItems.findIndex((prev) => prev.id === item.id);
            if (existingItemIndex === -1) {
                // Otherwise, add the new item
                return [...prevItems, item];
            } else {
                // If the item already exists, remove it
                return prevItems.filter((_, index) => index !== existingItemIndex);
            }
        })(selectedItems);

        setSelectedItems(_items);
        onValueChange?.(_items);
    };

    return (
        <div ref={ref} className={className}>
            {isDesktop ? (
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
                    <PopoverContent
                        className="p-0"
                        side="bottom"
                        align="start"
                        style={{
                            minWidth: 'var(--radix-popover-trigger-width)',
                            width: 'var(--radix-popover-trigger-width)',
                        }}
                    >
                        <Command shouldFilter={false}>
                            <EntitySearchContent
                                entities={entities}
                                onItemSelected={handleItemSelected}
                                search={search}
                                setSearch={setSearch}
                                inputPlaceholder={inputPlaceholder}
                                tracking={tracking}
                            />
                        </Command>
                    </PopoverContent>
                </Popover>
            ) : (
                <>
                    <Slot
                        onClick={() => {
                            setOpen(!open);
                        }}
                    >
                        {triggerContent}
                    </Slot>
                    <CommandDialog
                        open={open}
                        onOpenChange={setOpen}
                        shouldFilter={false}
                        title={inputPlaceholder}
                    >
                        <EntitySearchContent
                            entities={entities}
                            onItemSelected={handleItemSelected}
                            search={search}
                            setSearch={setSearch}
                            inputPlaceholder={inputPlaceholder}
                            tracking={tracking}
                        />
                    </CommandDialog>
                </>
            )}
        </div>
    );
};

EntitySearchCombobox.EntitySearchContent = EntitySearchContent;
